"use client";

import { useCallback, useState, useTransition } from "react";

import { Bell, LoaderCircle } from "lucide-react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";

import { useNotifications } from "../hooks/use-notifications";
import {
  getNotificationInvalidateTags,
  notificationEntityClickConfig,
} from "../lib/notification-utils";
import type {
  NotificationEntityType,
  NotificationItem,
} from "../types/notification.types";
import { NotificationList } from "./notification-list";

function UnreadBadge({ count }: { count: number }) {
  const display = count > 99 ? "99+" : String(count);

  return (
    <span className="bg-primary text-primary-foreground absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
      {display}
    </span>
  );
}

export function NotificationBell() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [isNavigatingRoute, startRouteTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const [shouldCloseAfterNavigation, setShouldCloseAfterNavigation] =
    useState(false);
  const [activeNotificationId, setActiveNotificationId] = useState<
    number | undefined
  >(undefined);
  const {
    notifications,
    unreadCount,
    hasMore,
    loadMore,
    isFetchingMore,
    markNotificationRead,
    markAllNotificationsRead,
  } = useNotifications();

  const handleNotificationClick = useCallback(
    (notification: NotificationItem) => {
      if (isNavigatingRoute) return;

      if (!notification.is_read) {
        void markNotificationRead(notification.id);
      }

      const clickConfig =
        notificationEntityClickConfig[
          notification.entity_type as NotificationEntityType
        ];

      const tagsToInvalidate = getNotificationInvalidateTags(
        notification.entity_type,
        notification.entity_id,
      );
      if (tagsToInvalidate?.length) {
        dispatch(baseApi.util.invalidateTags(tagsToInvalidate));
      }

      if (!clickConfig) return;

      setActiveNotificationId(notification.id);
      setShouldCloseAfterNavigation(true);
      startRouteTransition(() => {
        router.push(clickConfig.toHref(notification.entity_id));
      });
    },
    [dispatch, isNavigatingRoute, markNotificationRead, router],
  );

  const resolvedOpen =
    open && (!shouldCloseAfterNavigation || isNavigatingRoute);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      setShouldCloseAfterNavigation(false);
      setActiveNotificationId(undefined);
    }
  }, []);

  return (
    <>
      <DropdownMenu
        modal={false}
        open={resolvedOpen}
        onOpenChange={handleOpenChange}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            className="relative"
            aria-label="Open notifications"
            disabled={isNavigatingRoute}
          >
            {isNavigatingRoute ? (
              <LoaderCircle className="size-4.5 animate-spin" />
            ) : (
              <Bell className="size-4.5" />
            )}
            {unreadCount > 0 ? <UnreadBadge count={unreadCount} /> : null}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="relative w-[24rem] overflow-hidden p-0"
        >
          <NotificationList
            notifications={notifications}
            onMarkRead={(id) => void markNotificationRead(id)}
            onNotificationClick={handleNotificationClick}
            onMarkAllRead={() => void markAllNotificationsRead()}
            onLoadMore={loadMore}
            hasMore={hasMore}
            isLoadingMore={isFetchingMore}
            isNavigatingRoute={isNavigatingRoute}
            activeLoadingNotificationId={activeNotificationId}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
